import { useState } from 'react';

import type {
  AnswerMap,
  AnswerValue,
} from '../features/declarations/application/declarationForm';
import { Alert, Button, Card, SelectField, TextAreaField, TextField } from '../shared/components';

import styles from './ScreenLayout.module.css';

/**
 * The medical declaration.
 *
 * Every question comes from the stored policy. Nothing here knows what is being
 * asked — add an item in settings and it appears; retire one and it stops being
 * offered (spec FR-016).
 */

/** The subset of a policy item this screen needs to render a field. */
export interface DeclarationField {
  readonly id: string;
  readonly labelAr: string;
  readonly helpAr?: string;
  readonly type: 'boolean' | 'text' | 'date' | 'select' | 'multiselect' | 'number';
  readonly required: boolean;
  readonly options?: readonly string[];
}

export interface DeclarationPageProps {
  readonly fields: readonly DeclarationField[];
  readonly answers: AnswerMap;
  readonly missingItemIds: readonly string[];
  readonly missingLabelsAr: readonly string[];
  /** Read-only once submitted; the committee owns it from then on (FR-019). */
  readonly readOnly: boolean;
  readonly submitted: boolean;
  readonly windowClosed: boolean;
  readonly reviewNoteAr: string | null;
  readonly saving: boolean;
  readonly savedAr: string | null;
  readonly errorAr: string | null;
  readonly onChange: (itemId: string, value: AnswerValue) => void;
  readonly onSaveDraft: () => void;
  readonly onSubmit: () => void;
}

export function DeclarationPage({
  fields,
  answers,
  missingItemIds,
  missingLabelsAr,
  readOnly,
  submitted,
  windowClosed,
  reviewNoteAr,
  saving,
  savedAr,
  errorAr,
  onChange,
  onSaveDraft,
  onSubmit,
}: DeclarationPageProps) {
  // Missing fields are only marked after an attempt. Showing every required
  // item in red before anyone has typed makes a blank form look broken.
  const [attempted, setAttempted] = useState(false);

  const disabled = readOnly || windowClosed;

  const errorFor = (id: string): string | undefined =>
    attempted && missingItemIds.includes(id) ? 'هذا الحقل مطلوب' : undefined;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    onSubmit();
  };

  return (
    <div className={styles.page}>
      <h1 className="visually-hidden">الإقرار الطبي</h1>

      <Card title="الإقرار الطبي" description="تُستخدم هذه البيانات لتقدير التغطية">
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}
        {savedAr ? <Alert severity="success">{savedAr}</Alert> : null}

        {submitted ? (
          <Alert severity="success" title="تم الاستلام">
            استلمنا إقرارك وهو بانتظار المراجعة. لم يعد بإمكانك تعديله.
          </Alert>
        ) : null}

        {windowClosed && !submitted ? (
          <Alert severity="warning">فترة التقديم مغلقة حاليًا.</Alert>
        ) : null}

        {reviewNoteAr ? (
          <Alert severity="warning" title="ملاحظة من اللجنة">
            {reviewNoteAr}
          </Alert>
        ) : null}

        {/* Naming every missing item at once. One at a time makes someone
            submit, fix, submit again (FR-018). */}
        {attempted && missingLabelsAr.length > 0 ? (
          <Alert severity="error" title="بيانات ناقصة">
            {`يلزم الإجابة على: ${missingLabelsAr.join(' · ')}`}
          </Alert>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.formGrid}>
            {fields.map((field) => (
              <div key={field.id} className={styles.spanAll}>
                <DeclarationFieldInput
                  field={field}
                  value={answers[field.id]}
                  error={errorFor(field.id)}
                  disabled={disabled}
                  onChange={(value) => {
                    onChange(field.id, value);
                  }}
                />
              </div>
            ))}

            {!disabled ? (
              <div className={styles.spanAll}>
                <div className={styles.actions}>
                  <Button type="submit" size="lg" fullWidth loading={saving}>
                    إرسال الإقرار
                  </Button>
                  {/* A draft save is what makes a long form survive a dropped
                      connection or a closed tab (FR-017). */}
                  <Button variant="secondary" fullWidth onClick={onSaveDraft} disabled={saving}>
                    حفظ كمسودة
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </form>
      </Card>
    </div>
  );
}

function DeclarationFieldInput({
  field,
  value,
  error,
  disabled,
  onChange,
}: {
  readonly field: DeclarationField;
  readonly value: AnswerValue | undefined;
  readonly error: string | undefined;
  readonly disabled: boolean;
  readonly onChange: (value: AnswerValue) => void;
}) {
  const shared = {
    label: field.labelAr,
    required: field.required,
    optional: !field.required,
    error,
    disabled,
    ...(field.helpAr === undefined ? {} : { hint: field.helpAr }),
  };

  switch (field.type) {
    case 'boolean':
      return (
        <SelectField
          {...shared}
          // '' distinguishes "not answered" from "answered no" — collapsing
          // them would make a deliberate "no" impossible to record.
          value={value === undefined ? '' : value ? 'yes' : 'no'}
          onChange={(event) => {
            onChange(event.target.value === 'yes');
          }}
        >
          <option value="" disabled>
            اختر…
          </option>
          <option value="yes">نعم</option>
          <option value="no">لا</option>
        </SelectField>
      );

    case 'select':
      return (
        <SelectField
          {...shared}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          <option value="" disabled>
            اختر…
          </option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
      );

    case 'date':
      return (
        <TextField
          {...shared}
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      );

    case 'number':
      return (
        <TextField
          {...shared}
          numeric
          inputMode="numeric"
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => {
            onChange(Number(event.target.value));
          }}
        />
      );

    case 'multiselect':
      // Rendered as comma-separated text until a real multi-select control
      // exists. Stated plainly rather than silently mis-rendered.
      return (
        <TextField
          {...shared}
          hint="افصل بين القيم بفاصلة"
          value={Array.isArray(value) ? value.join('، ') : ''}
          onChange={(event) => {
            onChange(
              event.target.value
                .split(/[،,]/)
                .map((part) => part.trim())
                .filter(Boolean),
            );
          }}
        />
      );

    case 'text':
      return (
        <TextAreaField
          {...shared}
          rows={3}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      );
  }
}
